import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  NgZone,
  OnDestroy,
  viewChild
} from '@angular/core';
import {MODE_FRAMES, resolvePreset} from 'thinking-orbs/engine';
import type {ModeOpts, OrbFrame, OrbSize, OrbState} from 'thinking-orbs/engine';

/**
 * Which ink the orb paints with.
 *
 * The engine's own vocabulary is the SUBSTRATE, not the ink: its `dark` flag means "this orb sits on a dark
 * background", which is exactly when it paints LIGHT dots. Keeping the engine's word here (rather than
 * inverting it to `'white' | 'black'`) is deliberate — one name for one concept all the way down — but it is
 * the single most confusing thing about this component, so it is named once, here.
 */
export type ThinkingOrbTheme = 'light' | 'dark';

/** An 8-bit sRGB colour. */
export interface OrbRgb {
  readonly r: number;
  readonly g: number;
  readonly b: number;
}

/**
 * The tints the orb can be painted in — the app's own theme tokens, plus `'none'` for the engine's greyscale.
 *
 * **`'none'` is the default and must stay the default**: every caller that does not ask for a tint keeps the
 * monochrome orb it has today, byte for byte (pinned by a spec that diffs the pixels against the engine's own
 * painter).
 */
export type ThinkingOrbTint = 'none' | keyof typeof ORB_TINT_CLASSES;

/**
 * Theme token → the Tailwind utility that carries its colour.
 *
 * **These strings must stay literal.** Tailwind v3 generates a class only if it finds the class name verbatim
 * while scanning `src/**\/*.{html,ts}` (see `tailwind.config.js` → `content`), so a name assembled at runtime
 * (`` `text-${token}` ``) would be purged out of the stylesheet and the probe below would find nothing.
 *
 * Why a class and not a constant: `tailwind.config.js` maps `theme-primary` onto Tailwind's pink scale, and
 * **that file is meant to be the single place the brand colour is decided**. A hex literal here would fork it
 * — re-theme the app and the orb would quietly keep the old pink. Tailwind v3 emits no `--color-*` custom
 * properties to read either (v4 would), and the only other canvas painters in this app — the analytics charts
 * — hard-code hex, which is precisely the fork this avoids. So the colour is read back off a throwaway
 * element that carries the real class; see {@link resolveTintColor}.
 */
export const ORB_TINT_CLASSES = {
  'theme-primary-400': 'text-theme-primary-400',
  'theme-primary-500': 'text-theme-primary-500',
  'theme-primary-600': 'text-theme-primary-600',
  'theme-primary-700': 'text-theme-primary-700'
} as const;

/**
 * How far the tint ramp reaches towards white at the near end and towards black at the far end, as a fraction.
 *
 * A tint cannot be applied as alpha: the orb is painted onto a `bg-gray-500 opacity-75` sheet, and a
 * translucent mid-pink over mid-grey is mid-grey. The depth cue therefore has to survive as LIGHTNESS, which
 * is what this spread buys — the near end goes pale, the far end goes deep, and the hue is carried all the way
 * through.
 *
 * 0.85 is chosen against the measured range of the loader's own mode: `ribbon` at 64 px emits `white` values
 * spanning 0.130 … 0.780 (p10 0.225, p50 0.412, p90 0.634), never the full 0 … 1. With 0.85 that lands
 * `theme-primary-700` (190, 24, 93) between roughly (100, 13, 49) and (231, 169, 195) — deep maroon through
 * pink to pale pink — instead of collapsing to near-black and near-white, which is what a spread of 1 would do
 * to the extremes of a mode that DOES reach 0 and 1.
 */
export const ORB_TINT_SPREAD = 0.85;

/**
 * Reads a Tailwind colour utility's actual colour out of the live stylesheet.
 *
 * A throwaway element carrying the class is measured and thrown away again, so `tailwind.config.js` stays the
 * one place the brand colour is decided. The element is wrapped in a carrier whose inline `color` is a
 * **sentinel**: `color` is inherited, so if the class produced no rule at all (the stylesheet is not loaded,
 * or the class was purged) the probe comes back wearing the sentinel and this returns `null` — which the
 * component treats as "no tint", i.e. it degrades to the monochrome orb rather than to some arbitrary colour.
 *
 * @param className The Tailwind utility to measure, e.g. `text-theme-primary-700`.
 * @returns The resolved colour, or `null` when the class resolved to nothing.
 */
export function resolveTintColor(className: string): OrbRgb | null {
  const carrier = document.createElement('div');
  // Off-screen and inert: this must never affect layout, hit-testing or the accessibility tree.
  carrier.setAttribute('aria-hidden', 'true');
  carrier.style.cssText =
    'position:absolute;left:-9999px;top:0;width:0;height:0;overflow:hidden;pointer-events:none;color:rgb(1, 2, 3)';

  const probe = document.createElement('span');
  probe.className = className;
  carrier.appendChild(probe);
  document.body.appendChild(carrier);

  const computedColor = getComputedStyle(probe).color;
  carrier.remove();

  // The sentinel came back untouched ⇒ the utility is not in the stylesheet.
  if (/^rgba?\(\s*1\s*,\s*2\s*,\s*3\b/.test(computedColor)) return null;

  const channels = computedColor.match(/-?[\d.]+/g);
  if (!channels || channels.length < 3) return null;
  return {r: Number(channels[0]), g: Number(channels[1]), b: Number(channels[2])};
}

/**
 * The ink a single mark is painted with — the one place the engine's greyscale is reinterpreted as colour.
 *
 * The engine's own rule is `v = round((dark ? 1 - white : white) * 255)` on all three channels; `white` is an
 * INK value, so **0 is the nearest mark** and the substrate decides which way that reads (bright on a dark
 * substrate, dark on a light one). That brightness `L` is computed here exactly as the engine computes it, so:
 *
 * * with `tint === null` this returns the engine's grey unchanged, to the byte;
 * * with a tint, every channel is a monotone non-decreasing function of `L`, which is what keeps **depth
 *   monotonic**: a mark with a higher `white` can never come out reading as nearer than one with a lower
 *   `white`, whatever the tint. Pinned by a spec that samples the ramp across its whole range rather than by
 *   looking at a frame.
 *
 * @param white The mark's ink value, clamped to 0 … 1 the way the engine clamps it.
 * @param dark Whether the orb sits on a dark substrate.
 * @param tint The tint, or `null` for the engine's greyscale.
 */
export function orbInk(white: number, dark: boolean, tint: OrbRgb | null): OrbRgb {
  const clamped = Math.min(1, Math.max(0, white));
  const brightness = dark ? 1 - clamped : clamped;

  if (!tint) {
    const grey = Math.round(brightness * 255);
    return {r: grey, g: grey, b: grey};
  }

  // −1 = the far end of the ramp, 0 = the token's own colour, +1 = the near end.
  const mix = (brightness - 0.5) * 2 * ORB_TINT_SPREAD;
  const ramp = (channel: number) =>
    Math.round(mix >= 0 ? channel + (255 - channel) * mix : channel * (1 + mix));
  return {r: ramp(tint.r), g: ramp(tint.g), b: ramp(tint.b)};
}

/**
 * Paints one finished {@link OrbFrame}, in the engine's own order: lines first, so nodes sit on their edges.
 *
 * This replaces the engine's `MODE_DRAWS` — which is only `paintFrame(ctx, MODE_FRAMES[mode](…), dark)` and
 * hard-codes greyscale — while keeping `MODE_FRAMES`, so **the geometry is still entirely the engine's**. The
 * frame arrives already z-sorted and radius-clamped, so it is drawn in array order and nothing is re-derived.
 */
export function paintOrbFrame(
  context: CanvasRenderingContext2D,
  frame: OrbFrame,
  dark: boolean,
  tint: OrbRgb | null
): void {
  for (const line of frame.lines) {
    const {r, g, b} = orbInk(line.white, dark, tint);
    context.strokeStyle = `rgba(${r},${g},${b},${line.a ?? 1})`;
    context.lineWidth = line.w;
    context.beginPath();
    context.moveTo(line.x1, line.y1);
    context.lineTo(line.x2, line.y2);
    context.stroke();
  }

  for (const dot of frame.dots) {
    const {r, g, b} = orbInk(dot.white, dark, tint);
    context.fillStyle = `rgba(${r},${g},${b},${dot.a ?? 1})`;
    context.beginPath();
    context.arc(dot.x, dot.y, dot.r, 0, Math.PI * 2);
    context.fill();
  }
}

/**
 * The exponent that turns a box-size ratio into the radius multiplier which preserves ink coverage.
 *
 * The engine scales dot radii by `radiusScale(size, rsPow)` with `rsPow: 0.6`, i.e. radii grow as `size^0.6`
 * while the box grows as `size^1` — deliberately, so a 20 px orb stays legible. The side effect is that **a
 * bigger orb is a sparser, thinner orb**: coverage goes as `size^(1.2 − 2) = size^-0.8`. Measured, at the
 * loader's `composing`/`ribbon` preset with the dot count fixed at 566 whatever the size:
 *
 * | size | coverage (Σπr²/size²) |
 * | --- | --- |
 * | 64 | 14.13 % |
 * | 96 | 10.21 % |
 * | 128 | 8.11 % |
 *
 * Multiplying the radii by `(size / preset)^0.4` cancels that exactly, which is the same thing as scaling the
 * 64 px DESIGN up geometrically: positions already scale exactly (measured error 0), and with the multiplier
 * the radii do too — mean error 0.005 px at 96 and 0.011 px at 128 against a literal `r × size/64`, the
 * residual sitting on the six marks clamped to the `rMin` floor. Restored coverage: 14.04 % at 96, 14.00 % at
 * 128, against 14.13 % at 64.
 */
export const ORB_DENSITY_EXPONENT = 0.4;

/**
 * The tuned preset a given rendered size is built from.
 *
 * Upstream ships exactly two tuned designs — 64 (chat-avatar scale) and 20 (inline-text scale) — with their
 * own lane, segment and ghost counts. Anything else is one of those two, scaled. The cut is their geometric
 * mean (√(20 × 64) ≈ 35.8), so each size is built from whichever design it is nearer to on a ratio scale,
 * which is the scale the radius law works on.
 */
export function tunedPresetSizeFor(size: number): OrbSize {
  return size < 36 ? 20 : 64;
}

/**
 * The radius multiplier that keeps a rendered size at its tuned preset's ink coverage.
 *
 * Exactly 1 at the tuned sizes, so **a caller asking for 64 or 20 gets byte-identical geometry to a caller
 * who switched compensation off** — the feature cannot perturb the two designs it is built from.
 */
export function densityScale(size: number, presetSize: OrbSize): number {
  return Math.pow(size / presetSize, ORB_DENSITY_EXPONENT);
}

/**
 * The draw options for a rendered size: the tuned preset's, with the radii rescaled when asked.
 *
 * Only the three radius keys are touched. `rSizeMul` looks like the obvious lever and is **inert** for this
 * mode — measured: scaling it alone leaves coverage at the uncompensated 10.21 % / 8.11 %. The preset object
 * is memoised and shared inside the engine, so it is spread rather than mutated.
 */
export function orbDrawOptions(opts: ModeOpts, scale: number): ModeOpts {
  if (scale === 1) return opts;
  return {
    ...opts,
    rBase: (opts['rBase'] ?? 0) * scale,
    rDepth: (opts['rDepth'] ?? 0) * scale,
    rMin: (opts['rMin'] ?? 0) * scale
  };
}

/**
 * The hard ceiling on the canvas backing store, in device pixels per CSS pixel.
 *
 * A 3x phone would otherwise give a 192x192 backing store for a 64px orb, i.e. 2.25x the fill cost of the
 * capped one, for a difference no eye resolves on a matte 2px dot. The loader is on screen precisely when the
 * device is already busy, so this is the one place where paying for the extra pixels is worst.
 */
export const MAX_DEVICE_PIXEL_RATIO = 2;

/**
 * The instant, in animation seconds, that a `prefers-reduced-motion: reduce` user is shown.
 *
 * Deliberately not 0. Frame zero is a mode's initial condition, which is the one instant no mode is tuned
 * for — a fraction of a second in is a pose the user would genuinely have seen. For the loader's own mode
 * (`ribbon`) the difference happens to be nil: measured over `MODE_FRAMES.ribbon`, t=0 and t=0.6 both yield
 * 566 dots spanning 49.9 x 48.8 px of a 64px frame. The constant is therefore insurance for the other eight
 * states, not a fix for an observed defect in this one.
 */
export const STATIC_FRAME_SECONDS = 0.6;

/**
 * The largest step the animation clock will take in one frame, in seconds.
 *
 * `requestAnimationFrame` does not fire while a tab is backgrounded, so the first frame after a return can be
 * minutes after the previous one. Without a clamp the orb would teleport through that whole interval in a
 * single frame — visible as a jerk exactly when the user looks back at it.
 */
const MAX_FRAME_DELTA_SECONDS = 0.25;

/**
 * Accessible name per state, used when the caller supplies none.
 *
 * German, formal register (`Sie`/`Ihr`) per the style guide — these are read out verbatim by a screen reader,
 * so they are on-screen copy even though no sighted user ever sees them.
 */
const DEFAULT_LABELS: Readonly<Record<OrbState, string>> = {
  working: 'Wird verarbeitet …',
  searching: 'Wird gesucht …',
  solving: 'Wird berechnet …',
  listening: 'Eingabe wird erfasst …',
  connecting: 'Verbindung wird hergestellt …',
  weaving: 'Daten werden verknüpft …',
  composing: 'Wird geladen …',
  breathing: 'Bitte warten …',
  shaping: 'Wird vorbereitet …'
};

/**
 * The effective backing-store scale for the current display: the device pixel ratio, capped.
 *
 * Exported so the cap is assertable as a pure function as well as through a mounted component — a spec that
 * can only observe the mounted canvas cannot distinguish "capped" from "this machine happens to be 1x".
 *
 * @param devicePixelRatio The raw ratio; defaults to the current window's. A non-finite or non-positive value
 *   (some headless and embedded surfaces report 0) degrades to 1 rather than producing a 0x0 backing store.
 */
export function backingStoreScale(devicePixelRatio: number = window.devicePixelRatio): number {
  const raw = Number.isFinite(devicePixelRatio) && devicePixelRatio > 0 ? devicePixelRatio : 1;
  return Math.min(raw, MAX_DEVICE_PIXEL_RATIO);
}

/**
 * A dotted "thinking" orb, painted on a plain 2D canvas by the `thinking-orbs` engine.
 *
 * ### Why the engine and not the package root
 * `thinking-orbs` ships two entry points. The default one is a **React** component; `thinking-orbs/engine` is
 * pure geometry plus a 2D-canvas painter with no React import and no DOM dependency. React is only a
 * `peerDependency` of the package, so importing the engine subpath never pulls React into the bundle. Import
 * the root here and the whole of React lands in the web app. Don't.
 *
 * ### What this component owns
 * The engine hands over pure geometry (`MODE_FRAMES`) and does nothing else: it does not create a canvas,
 * does not clear one, does not know about `devicePixelRatio`, and has no clock. Its ready-made painter
 * (`MODE_DRAWS`) is deliberately NOT used — it is only `paintFrame(ctx, MODE_FRAMES[mode](…), dark)` with the
 * greyscale hard-coded, and owning that last step is what makes {@link tint} possible without forking the
 * geometry. Everything around it is this component's job, and each piece is a way the loader could misbehave:
 *
 * * **The clock runs outside Angular.** A `requestAnimationFrame` loop registered inside the zone schedules a
 *   change-detection pass 60 times a second for the entire lifetime of the loader — which is on screen
 *   precisely when the app is already struggling. {@link NgZone.runOutsideAngular} is not an optimisation
 *   here, it is the difference between a loading indicator and a second load.
 * * **The loop stops when nothing can see it.** Backgrounded tab (`document.hidden`) and scrolled out of view
 *   (`IntersectionObserver`) both park it. Resuming continues from the same phase, because the animation clock
 *   only advances while the loop runs — a paused orb does not silently fast-forward.
 * * **`prefers-reduced-motion: reduce` gets one frame and no loop at all.** Not a slower animation: none.
 * * **A missing 2D context is survivable.** `getContext('2d')` can return `null` (a blocked or exhausted
 *   canvas context, some headless surfaces). The orb then paints nothing and the app carries on; a throwing
 *   loading indicator would take down the screen it is supposed to be reassuring the user about.
 *
 * ### Accessibility
 * The canvas is a `role="img"` with an `aria-label`, so a screen reader announces the state rather than
 * meeting an unlabelled graphic. When the orb sits inside something that already names it — the app-wide
 * loader's `role="status"` region with its own `sr-only` text — hide the orb from assistive technology at the
 * call site (`aria-hidden` on its wrapper) instead of stripping the label here; standalone use must stay
 * labelled by default.
 *
 * @example
 * ```html
 * <app-thinking-orb state="composing" [size]="64" theme="dark" />
 * ```
 */
@Component({
  selector: 'app-thinking-orb',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // The CSS size is bound on the <canvas> ITSELF, never only on a wrapper: a canvas with a backing store but
  // no CSS box falls back to its intrinsic 300x150, and one sized only by its parent does not shrink-wrap.
  // This is Bug #218's shape ("the indicator has no box, so nothing is visible") in canvas form.
  template: `
    <canvas #surface
            role="img"
            [attr.aria-label]="accessibleLabel()"
            [style.width.px]="size()"
            [style.height.px]="size()"
            class="block"></canvas>
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `
  ]
})
export class ThinkingOrbComponent implements AfterViewInit, OnDestroy {
  /** Which of the engine's nine tuned animations to show. */
  readonly state = input<OrbState>('composing');

  /**
   * Rendered size in CSS pixels. Any size is allowed; 64 and 20 are the two tuned designs.
   *
   * Upstream's own type is `64 | 20` and its reason is real — they carry their own lane, segment and ghost
   * counts, so they are separate designs rather than one design at two scales. Everything else is therefore
   * built from whichever of the two it is nearer to ({@link tunedPresetSizeFor}) and rescaled
   * ({@link compensateDensity}). **64 and 20 are byte-identical to the upstream presets either way.**
   */
  readonly size = input(64);

  /**
   * Which substrate the orb is painted onto — see {@link ThinkingOrbTheme}.
   *
   * Defaults to `'dark'`, i.e. **light ink**, because the app-wide loader paints onto its dimmed
   * `bg-gray-500` sheet.
   */
  readonly theme = input<ThinkingOrbTheme>('dark');

  /**
   * Which theme token to tint the marks with, or `'none'` for the engine's greyscale.
   *
   * `'none'` is the default on purpose: adding a tint must not repaint every existing usage. See
   * {@link ORB_TINT_CLASSES} for why the value is a token and not a colour.
   */
  readonly tint = input<ThinkingOrbTint>('none');

  /**
   * Whether an off-preset {@link size} keeps its preset's ink coverage instead of thinning out.
   *
   * On by default: the engine grows radii as `size^0.6` while the box grows as `size^1`, so an uncompensated
   * 128 px orb is a *sparser, thinner* orb rather than a bigger one (8.11 % coverage against 64's 14.13 %,
   * measured). Switch it off to see that untouched — {@link ORB_DENSITY_EXPONENT} has the numbers. **At 64
   * and 20 the switch does nothing at all**, because the multiplier is exactly 1 there.
   */
  readonly compensateDensity = input(true);

  /** Freeze the animation on the current frame without tearing the canvas down. */
  readonly paused = input(false);

  /** Accessible name. Blank falls back to {@link DEFAULT_LABELS} for the current {@link state}. */
  readonly label = input('');

  private readonly surface = viewChild.required<ElementRef<HTMLCanvasElement>>('surface');
  private readonly zone = inject(NgZone);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);

  /** The announced name: the caller's, else the state's German default. */
  protected readonly accessibleLabel = computed(() => this.label().trim() || DEFAULT_LABELS[this.state()]);

  /**
   * Mode, speed and draw options for the current `(state, size)` pair: the tuned preset the size is built
   * from (memoised by the engine), with the radii rescaled when {@link compensateDensity} asks for it.
   */
  private readonly resolved = computed(() => {
    const size = this.size();
    const presetSize = tunedPresetSizeFor(size);
    const preset = resolvePreset(this.state(), presetSize);
    const scale = this.compensateDensity() ? densityScale(size, presetSize) : 1;
    return {...preset, opts: orbDrawOptions(preset.opts, scale)};
  });

  /**
   * The resolved tint, or `null` for the engine's greyscale.
   *
   * A `computed` rather than a call from the paint loop: the probe forces a style recalculation, so it must
   * run when the token changes and never once per frame.
   */
  private readonly tintColor = computed<OrbRgb | null>(() => {
    const token = this.tint();
    return token === 'none' ? null : resolveTintColor(ORB_TINT_CLASSES[token]);
  });

  /** `true` ⇒ light ink. See {@link ThinkingOrbTheme}. */
  private readonly darkSubstrate = computed(() => this.theme() === 'dark');

  private context: CanvasRenderingContext2D | null = null;
  private frameHandle: number | null = null;
  private observer: IntersectionObserver | null = null;
  private motionQuery: MediaQueryList | null = null;

  /** Seconds of animation actually SHOWN. Only advances while the loop runs, which is what keeps phase. */
  private elapsedSeconds = 0;
  /** The previous frame's timestamp, or `null` when the loop is not running. */
  private lastTimestamp: number | null = null;

  private viewReady = false;
  private onScreen = true;

  constructor() {
    // Re-paint and, where needed, re-size when a rendering input changes. This deliberately does not start or
    // stop the clock on its own — `syncLoop()` decides that from the full set of conditions, so `paused`
    // flipping and the tab being hidden cannot fight over the loop.
    effect(() => {
      this.resolved();
      this.darkSubstrate();
      this.tintColor();
      this.paused();
      if (!this.viewReady) return;

      this.resizeBackingStore();
      this.paintFrame();
      this.syncLoop();
    });
  }

  /**
   * Sets the canvas up and paints the first frame synchronously, then starts the clock.
   *
   * The first frame is painted here rather than left to the first `requestAnimationFrame` callback on purpose:
   * a canvas that is present, correctly sized and blank is exactly the "backdrop with no indicator" symptom
   * the loader has produced three times before, and it would be real for one frame on every open.
   */
  ngAfterViewInit(): void {
    const canvas = this.surface().nativeElement;
    // `getContext` returns null on a surface that cannot give a 2D context. Degrade to a blank canvas: a
    // loading indicator that throws is strictly worse than one that is merely invisible.
    this.context = canvas.getContext('2d');
    this.viewReady = true;
    if (!this.context) return;

    // Everything below registers browser callbacks. Registered inside the zone, each one schedules a
    // change-detection pass — the rAF loop would schedule one per frame, forever.
    this.zone.runOutsideAngular(() => {
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.motionQuery.addEventListener('change', this.onMotionPreferenceChange);
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      // `IntersectionObserver` is universally available in the app's browser floor (iOS 14.5+), but guard
      // anyway: an orb that never animates is a far better failure than one that cannot mount.
      if (typeof IntersectionObserver !== 'undefined') {
        this.observer = new IntersectionObserver(entries => {
          this.onScreen = entries.some(entry => entry.isIntersecting);
          this.syncLoop();
        });
        this.observer.observe(this.host.nativeElement);
      }

      this.resizeBackingStore();
      // A reduced-motion user is shown one representative instant and no clock at all.
      this.elapsedSeconds = this.prefersReducedMotion() ? STATIC_FRAME_SECONDS : 0;
      this.paintFrame();
      this.syncLoop();
    });
  }

  ngOnDestroy(): void {
    this.stopLoop();
    this.observer?.disconnect();
    this.observer = null;
    this.motionQuery?.removeEventListener('change', this.onMotionPreferenceChange);
    this.motionQuery = null;
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  /** Whether the clock may run right now. Every condition is a reason NOT to burn a frame. */
  private shouldAnimate(): boolean {
    return (
      this.viewReady &&
      this.context !== null &&
      !this.paused() &&
      !this.prefersReducedMotion() &&
      !document.hidden &&
      this.onScreen
    );
  }

  private prefersReducedMotion(): boolean {
    // `.matches` is live, so the preference is re-read on every decision rather than cached at mount.
    return this.motionQuery?.matches ?? false;
  }

  /** Brings the loop in line with {@link shouldAnimate}. Idempotent in both directions. */
  private syncLoop(): void {
    if (this.shouldAnimate()) {
      this.startLoop();
    } else {
      this.stopLoop();
    }
  }

  private startLoop(): void {
    if (this.frameHandle !== null) return;
    // Registered outside the zone so no frame ever schedules change detection. `ngAfterViewInit` already runs
    // outside it, but a later `syncLoop()` (visibility change, an input effect) may not.
    this.zone.runOutsideAngular(() => {
      this.frameHandle = requestAnimationFrame(this.tick);
    });
  }

  private stopLoop(): void {
    if (this.frameHandle !== null) {
      cancelAnimationFrame(this.frameHandle);
      this.frameHandle = null;
    }
    // Dropping the reference is what makes a resume continue in phase: the next frame contributes no delta
    // for the whole interval the orb was parked.
    this.lastTimestamp = null;
  }

  private readonly tick = (timestamp: number): void => {
    const delta = this.lastTimestamp === null ? 0 : (timestamp - this.lastTimestamp) / 1000;
    this.lastTimestamp = timestamp;
    this.elapsedSeconds += Math.min(Math.max(delta, 0), MAX_FRAME_DELTA_SECONDS);
    this.paintFrame();
    this.frameHandle = requestAnimationFrame(this.tick);
  };

  private readonly onVisibilityChange = (): void => this.syncLoop();

  private readonly onMotionPreferenceChange = (): void => {
    // A user switching reduced motion ON mid-animation must land on the representative frame, not wherever
    // the clock happened to be — otherwise they keep the degenerate pose they were on.
    if (this.prefersReducedMotion()) {
      this.elapsedSeconds = STATIC_FRAME_SECONDS;
      this.paintFrame();
    }
    this.syncLoop();
  };

  /**
   * Matches the canvas backing store to its CSS box at the capped device pixel ratio.
   *
   * Writing `width`/`height` resets the context's transform and clears it, which is why the transform is
   * re-established in {@link paintFrame} on every frame rather than once here.
   */
  private resizeBackingStore(): void {
    const canvas = this.surface().nativeElement;
    const pixels = Math.round(this.size() * backingStoreScale());
    if (canvas.width !== pixels) canvas.width = pixels;
    if (canvas.height !== pixels) canvas.height = pixels;
  }

  /** Paints one frame of the current preset at the current animation instant. */
  private paintFrame(): void {
    const context = this.context;
    if (!context) return;

    const size = this.size();
    const scale = backingStoreScale();
    // The engine draws in CSS pixels and knows nothing about the backing store, so the scale lives here.
    context.setTransform(scale, 0, 0, scale, 0, 0);
    // The engine only ADDS dots; without this the orb smears into a solid disc within a second.
    context.clearRect(0, 0, size, size);

    const {mode, speed, opts} = this.resolved();
    // `MODE_FRAMES` + our own painter, never `MODE_DRAWS`: the geometry stays the engine's, the ink is ours.
    const frame = MODE_FRAMES[mode](size, this.elapsedSeconds * speed, opts);
    paintOrbFrame(context, frame, this.darkSubstrate(), this.tintColor());
  }
}
